import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { App } from './App';
import { ModuleProvider } from './modules/ModuleProvider';
import { AuthProvider } from './auth/AuthProvider';
import { subscribeToSession } from './auth/session';
import { ThemeProvider } from './theme/ThemeProvider';
import { ConfirmProvider } from './ui/ConfirmProvider';
import { ToastProvider } from './ui/ToastProvider';
import './styles/base.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Cached answers belong to the principal they were fetched as.
 *
 * React Query keeps results by key for the life of the page, so without this a second sign-in in
 * the same tab could render the previous workspace's data — from cache, while its own request was
 * still in flight, or after that request was refused. Discarding the cache whenever the session
 * identity changes makes a workspace switch start from nothing, which is the only safe default for
 * a multi-tenant client.
 */
subscribeToSession((_user, accessChanged) => {
  if (!accessChanged) return;
  void queryClient.cancelQueries();
  queryClient.clear();
});

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container #root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ConfirmProvider>
            <BrowserRouter useTransitions={false}>
              <AuthProvider>
                <ModuleProvider>
                  <App />
                </ModuleProvider>
              </AuthProvider>
            </BrowserRouter>
          </ConfirmProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
